import React from "react";
import {
  Badge,
  Body1,
  Button,
  Tooltip,
  makeStyles,
  shorthands,
  tokens,
} from "@fluentui/react-components";
import { DeleteRegular } from "@fluentui/react-icons";
import MaterialReactTable, { MRT_ColumnDef } from "material-react-table";
import { SavedReport, SAVED_REPORTS_LIMIT } from "../hooks/useSavedReports";

const useStyles = makeStyles({
  // Sits inside MRT's top toolbar (a flex row), so it needs to stay a block
  // and be allowed to shrink rather than force the toolbar to overflow.
  head: {
    minWidth: 0,
    flexShrink: 1,
  },
  title: {
    display: "flex",
    alignItems: "center",
    columnGap: "8px",
    margin: 0,
  },
  description: {
    display: "block",
    marginTop: "4px",
    color: tokens.colorNeutralForeground3,
  },
  empty: {
    ...shorthands.padding("16px"),
    color: tokens.colorNeutralForeground3,
  },
  // Long names (up to 255 chars, possibly with no spaces) wrap onto the next
  // lines instead of widening the table. `anywhere` also breaks a single
  // unbroken word, which normal wrapping would leave overflowing.
  name: {
    display: "block",
    whiteSpace: "normal",
    overflowWrap: "anywhere",
  },
});

// Width of the Action (delete) column. The name column gets the rest.
const ACTION_COLUMN_WIDTH = 90;

interface SavedReportsPanelProps {
  list: SavedReport[];
  total: number;
  isLoading: boolean;
  error: string | null;
  // The row currently being deleted (post-confirmation), if any — disables
  // just that row's delete button so a second click can't fire a second
  // delete request while the first is still in flight.
  deletingId?: string | null;
  onLoadSavedReport: (row: SavedReport) => void;
  onDeleteSavedReport: (row: SavedReport) => void;
}

/**
 * "My Reports" — the named parameter sets saved against the currently selected
 * report. Renders the mockup's `.versions-card`: header + count badge +
 * description, a search box, and a table of saved names with a delete action.
 *
 * Built on Material React Table, the same table this page already uses for
 * PopupTable, so search/sort/empty-state behaviour matches the rest of the
 * report screen.
 *
 * Search is MRT's own global filter, which is client-side by default — the
 * whole list is already in memory (the backend caps it at SAVED_REPORTS_LIMIT
 * rows), so typing in it sends no request. It is shown expanded rather than
 * behind the search icon, matching the mockup's always-visible input.
 */
const SavedReportsPanel: React.FC<SavedReportsPanelProps> = ({
  list,
  total,
  isLoading,
  error,
  deletingId,
  onLoadSavedReport,
  onDeleteSavedReport,
}) => {
  const styles = useStyles();

  const columns = React.useMemo<MRT_ColumnDef<SavedReport>[]>(
    () => [
      {
        accessorKey: "SavedReportParamName",
        header: "Saved Names",
        // Override MRT's size-based width so, under the table's fixed layout,
        // this column takes whatever the Action column leaves.
        muiTableHeadCellProps: { sx: { width: "auto", minWidth: 0 } },
        muiTableBodyCellProps: { sx: { width: "auto", minWidth: 0 } },
        // A <span>, not a <p> — a paragraph element's default margins would
        // otherwise pad the cell vertically for no reason.
        Cell: ({ cell }) => {
          const name = String(cell.getValue() ?? "");
          return (
            <span className={styles.name}>{name}</span>
          );
        },
      },
    ],
    [styles.name],
  );

  return (
    <div data-testid="saved-reports-panel">
      {error && <div className={styles.empty}>{error}</div>}

      {!error && (
        <MaterialReactTable
          // The outer Paper is the table's own card surface — padding and
          // corner radius belong here rather than on a wrapper div, so the
          // toolbar and the table body sit inside the same rounded box.
          muiTablePaperProps={{
            sx: {
              padding: "10px",
              borderRadius: "4px",
            },
          }}
          muiTableContainerProps={{
            sx: {
              height: "calc(100vh - 475px) !important",
              overflowX: "hidden",
            },
          }}
          // Fixed layout pins the table to the panel's width, so a long name
          // wraps (see styles.name) rather than pushing the Action
          // column off-screen behind a horizontal scrollbar.
          muiTableProps={{
            sx: { tableLayout: "fixed", width: "100%" },
          }}
          columns={columns}
          data={list}
          // Trimmed to the same controls PopupTable keeps — this is a short,
          // single-column list, not a data grid.
          enableColumnFilters={false}
          enableColumnActions={false}
          enableDensityToggle={false}
          enableFullScreenToggle={false}
          enableHiding={false}
          enableSorting={false}
          enablePagination={false}
          enableTopToolbar
          positionToolbarAlertBanner="none"
          enableRowActions
          positionActionsColumn="last"
          displayColumnDefOptions={{
            "mrt-row-actions": { header: "Action", size: ACTION_COLUMN_WIDTH },
          }}
          // The card header lives in the table's own top toolbar rather than
          // above it. Takes a render FUNCTION returning a single node, so the
          // title and description are wrapped in one element.
          renderTopToolbarCustomActions={() => (
            <div className={styles.head}>
              <h3 className={styles.title}>
                My Reports
                <Badge appearance="tint" data-testid="saved-reports-count">
                  {`${total}/${SAVED_REPORTS_LIMIT}`}
                </Badge>
              </h3>
              <Body1 className={styles.description}>
                Named parameter sets saved for this report — select one to load
                its parameters back onto the form. Up to {SAVED_REPORTS_LIMIT}{" "}
                per report.
              </Body1>
            </div>
          )}
          // MRT's DEFAULT puts the global-filter search box at the LEFT of the
          // top toolbar, rendered BEFORE renderTopToolbarCustomActions — i.e.
          // [search] [title + description] [buttons]. Explicitly "right"
          // here instead, to match the mockup's [title] ... [search] order.
          positionGlobalFilter="right"
          initialState={{ showGlobalFilter: true }}
          state={{ isLoading }}
          muiSearchTextFieldProps={{
            placeholder: "Search Saved Reports...",
            variant: "outlined",
            size: "small",
          }}
          // Clicking anywhere on the row loads that saved report onto the
          // form. Also reachable from the keyboard: the row is a focusable,
          // ARIA-labelled button (tabIndex + role="button"), and Enter/Space
          // trigger the same load — mouse-only before this.
          muiTableBodyRowProps={({ row }) => ({
            onClick: () => onLoadSavedReport(row.original),
            onKeyDown: (event: React.KeyboardEvent) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onLoadSavedReport(row.original);
              }
            },
            tabIndex: 0,
            role: "button",
            "aria-label": `Load saved report ${row.original.SavedReportParamName}`,
            sx: { cursor: "pointer" },
            "data-testid": `saved-report-row-${row.original.SavedReportParamId}`,
          })}
          renderRowActions={({ row }) => (
            <Tooltip content="Delete saved report" relationship="label">
              <Button
                appearance="subtle"
                icon={<DeleteRegular />}
                aria-label={`Delete ${row.original.SavedReportParamName}`}
                disabled={deletingId === row.original.SavedReportParamId}
                // The row itself loads the saved report, so the delete button
                // must not also trigger that.
                onClick={(event) => {
                  event.stopPropagation();
                  onDeleteSavedReport(row.original);
                }}
                data-testid={`saved-report-delete-${row.original.SavedReportParamId}`}
              />
            </Tooltip>
          )}
          renderEmptyRowsFallback={() => {
            return (
              <div
                data-testid="saved-reports-empty"
                className={styles.empty}
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "calc(100vh - 530px)",
                }}
              >
                No saved reports yet — fill in the parameters above, name them,
                and select "Save as My Reports".
              </div>
            );
          }}
        />
      )}
    </div>
  );
};

export default SavedReportsPanel;
